import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQuery } from "convex/react";
import type { FunctionReturnType } from "convex/server";
import { api, type Id } from "../lib/api";
import { useCompany } from "../lib/company";
import { JURISDICTIONS, type Jurisdiction } from "../../convex/model/enums";
import {
  legislationBrowserRows,
  legislationBrowserRowView,
  legislationBrowserSectionSearchView,
  sectionSearchMatchCount,
  legislationBrowserTypeFilterFromInput,
  legislationBrowserTypeFilterToInput,
  obligationExportCsv,
  obligationExportFilename,
  obligationExportRow,
  type LegislationBrowserSectionSearchView,
  type LegislationBrowserTypeFilter,
} from "../domain/legislation";
import { subscriptionLegislationIds } from "../domain/subscriptions";
import { caughtErrorMessage } from "../domain/errors";
import {
  OBLIGATION_SET_ITEM_STATUSES,
  obligationSetDefaultName,
  obligationSetItemStatusLabel,
  type ObligationSetItemInput,
  type ObligationSetItemStatus,
} from "../domain/obligationSets";
import { formatCountLabel } from "../domain/text";
import {
  structuredSearchWorkbookBytes,
  structuredSearchWorkbookFilename,
} from "../domain/searchExportWorkbook";
import { Button, Card, Icon, JurisBadge, StatusBadge, TypeBadge, cx, jurisShort } from "../components/ui";
import { downloadBinaryFile, downloadTextFile } from "../lib/download";
import { SearchText } from "../components/SearchText";
import { useAccess } from "../lib/access";
import { useCompleteSourceSearch } from "../lib/useCompleteSourceSearch";
import { matchingSourceBytes } from "../domain/matchingSourceWorkbook";

type SectionSearchResult = FunctionReturnType<typeof api.sections.search>[number];
type VisibleSectionSearchView = Exclude<LegislationBrowserSectionSearchView<SectionSearchResult>, { readonly kind: "hidden" }>;
type SelectedObligationDraft = {
  readonly ownerName: string;
  readonly status: ObligationSetItemStatus;
  readonly evidenceControlReference: string;
  readonly notes: string;
};
type ObligationSetSaveState = "idle" | "saving" | "saved" | "error";

const emptySelectedObligationDraft = (): SelectedObligationDraft => ({
  ownerName: "",
  status: "needsReview",
  evidenceControlReference: "",
  notes: "",
});

const SELECTED_OBLIGATION_FIELD_CLASS = "h-8 w-full rounded-sm border border-slate-300 bg-white px-2 text-[12.5px] font-medium normal-case tracking-normal text-slate-700 outline-none placeholder:text-slate-400 focus:border-[color:var(--accent)] focus:ring-1 focus:ring-[color:var(--accent)]";

const TYPE_FILTER_OPTIONS = [
  { value: "ALL", label: "All types" },
  { value: "act", label: "Acts" },
  { value: "regulation", label: "Regulations" },
] as const;

export function LegislationBrowser() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { companyId } = useCompany();
  const { canWrite } = useAccess();
  const createObligationSet = useMutation(api.obligationSets.create);
  const urlSearch = searchParams.get("search") ?? "";
  const [q, setQ] = useState(urlSearch);
  const [juris, setJuris] = useState<Jurisdiction | "ALL">("ALL");
  const [type, setType] = useState<LegislationBrowserTypeFilter>({ kind: "all" });
  const [subOnly, setSubOnly] = useState(false);
  const [selectedSectionIds, setSelectedSectionIds] = useState<ReadonlySet<Id<"sections">>>(() => new Set());
  const [selectedDrafts, setSelectedDrafts] = useState<Readonly<Record<string, SelectedObligationDraft>>>({});
  const [obligationSetName, setObligationSetName] = useState("");
  const [saveState, setSaveState] = useState<ObligationSetSaveState>("idle");
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedObligationSetId, setSavedObligationSetId] = useState<Id<"obligationSets"> | null>(null);

  const searchText = q.trim();
  const legislation = useQuery(api.legislation.list, juris === "ALL" ? {} : { jurisdiction: juris });
  const subs = useQuery(api.subscriptions.listForCompany, companyId !== null ? { companyId } : "skip");
  const completeSearch = useCompleteSourceSearch(companyId, searchText, juris === "ALL" ? undefined : juris, type.kind === "all" ? undefined : type.legislationKind);
  const sectionMatches = completeSearch.matches;
  const structuredExport = useQuery(
    api.searchExports.structured,
    searchText.length >= 2 && companyId !== null
      ? { companyId, query: searchText }
      : "skip",
  );
  const subscribedIds = useMemo(() => subscriptionLegislationIds(subs ?? []), [subs]);

  const rows = useMemo(
    () => legislationBrowserRows({
      legislation: legislation ?? [],
      subscribedIds,
      type,
      subscribedOnly: subOnly,
      query: q,
    }),
    [legislation, q, subOnly, subscribedIds, type],
  );

  const sectionSearchView = useMemo(
    () => legislationBrowserSectionSearchView({ query: q, matches: sectionMatches }),
    [q, sectionMatches],
  );
  const selectedSectionMatches = useMemo(
    () => sectionSearchView.kind === "ready"
      ? sectionSearchView.matches.filter((match) => selectedSectionIds.has(match.section._id))
      : [],
    [sectionSearchView, selectedSectionIds],
  );
  const obligationHitCount = sectionSearchMatchCount(sectionMatches);

  useEffect(() => {
    setQ(urlSearch);
  }, [urlSearch]);

  useEffect(() => {
    setSelectedSectionIds(new Set());
    setSelectedDrafts({});
    setObligationSetName("");
    setSaveState("idle");
    setSaveError(null);
    setSavedObligationSetId(null);
  }, [searchText]);

  const toggleSectionSelection = (sectionId: Id<"sections">) => {
    const selected = selectedSectionIds.has(sectionId);
    setSelectedSectionIds((current) => {
      const next = new Set(current);
      if (selected) next.delete(sectionId);
      else next.add(sectionId);
      return next;
    });
    setSelectedDrafts((current) => {
      const key = String(sectionId);
      if (selected) {
        const { [key]: _removed, ...rest } = current;
        return rest;
      }
      return { ...current, [key]: emptySelectedObligationDraft() };
    });
    setSaveState("idle");
    setSaveError(null);
    setSavedObligationSetId(null);
  };

  const updateSelectedDraft = (sectionId: Id<"sections">, patch: Partial<SelectedObligationDraft>) => {
    setSelectedDrafts((current) => {
      const key = String(sectionId);
      const existing = current[key] ?? emptySelectedObligationDraft();
      return { ...current, [key]: { ...existing, ...patch } };
    });
    setSaveState("idle");
    setSaveError(null);
    setSavedObligationSetId(null);
  };

  const updateObligationSetName = (name: string) => {
    setObligationSetName(name);
    setSaveState("idle");
    setSaveError(null);
    setSavedObligationSetId(null);
  };

  const selectedTopic = sectionSearchView.kind === "ready" ? sectionSearchView.query : searchText;

  const exportSelectedObligations = () => {
    if (selectedSectionMatches.length === 0) return;
    const rows = selectedSectionMatches.map((match) => {
      const draft = selectedDrafts[String(match.section._id)] ?? emptySelectedObligationDraft();
      return obligationExportRow({
        match,
        topic: selectedTopic,
        responsiblePerson: draft.ownerName,
        status: obligationSetItemStatusLabel(draft.status),
        evidenceControlReference: draft.evidenceControlReference,
        notes: draft.notes,
        sourceUrl: window.location.origin + "/legislation/" + match.legislation._id + "?section=" + match.section._id,
      });
    });
    downloadTextFile({
      filename: obligationExportFilename(searchText),
      text: obligationExportCsv(rows),
    });
  };

  const exportStructuredResults = () => {
    if (structuredExport === undefined || structuredExport.rows.length === 0) return;
    downloadBinaryFile({
      filename: structuredSearchWorkbookFilename(searchText),
      bytes: structuredSearchWorkbookBytes(structuredExport, window.location.origin),
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
  };

  const exportMatchingResults = () => {
    if (!sectionMatches) return;
    downloadBinaryFile({ filename: "lawstream-matching-source-clauses.xlsx", bytes: matchingSourceBytes(sectionMatches, searchText, window.location.origin), type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
  };

  const saveSelectedObligationSet = () => {
    if (companyId === null || selectedSectionMatches.length === 0 || saveState === "saving") return;
    const name = obligationSetName.trim() === "" ? obligationSetDefaultName(selectedTopic) : obligationSetName;
    setSaveState("saving");
    setSaveError(null);
    setSavedObligationSetId(null);
    const save = async () => {
      try {
        const obligationSetId = await createObligationSet({
          companyId,
          name,
          query: selectedTopic,
          items: selectedSectionMatches.map((match) => obligationSetItemFromMatch({
            match,
            topic: selectedTopic,
            draft: selectedDrafts[String(match.section._id)] ?? emptySelectedObligationDraft(),
          })),
        });
        setObligationSetName(name);
        setSavedObligationSetId(obligationSetId);
        setSaveState("saved");
      } catch (error) {
        setSaveState("error");
        setSaveError(caughtErrorMessage(error, "Could not save obligation set"));
      }
    };
    void save();
  };

  return (
    <div className="mx-auto w-full max-w-[1320px] px-4 py-5 sm:px-6 lg:px-8">
      <header className="mb-5 border-b border-slate-300 pb-5">
        <div className="flex flex-col justify-between gap-4 lg:flex-row lg:items-end">
          <div className="max-w-3xl">
            <h1 className="font-serif text-2xl font-semibold tracking-[-0.02em] text-slate-950 sm:text-[28px]">Legislation source register</h1>
            <p className="mt-1.5 max-w-2xl text-[13px] leading-5 text-slate-600">
              Query statutory text, isolate obligation clauses, and stage source evidence for the document mapping workspace.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-y-2 text-[12px] text-slate-600 lg:max-w-[560px] lg:justify-end" role="group" aria-label="Source register summary">
            <RegisterReadout label="Instruments" value={legislation === undefined ? "Loading…" : String(rows.length)} />
            <RegisterReadout label="Matching clauses" value={searchText.length < 2 ? "—" : obligationHitCount === null ? "Searching…" : String(obligationHitCount)} />
            <RegisterReadout label="Selected clauses" value={String(selectedSectionMatches.length)} active={selectedSectionMatches.length > 0} />
            <RegisterReadout label="Monitored sources" value={subs === undefined ? "Loading…" : String(subscribedIds.size)} />
          </div>
        </div>
      </header>

      <section aria-labelledby="source-query-heading" className="mb-5 border border-slate-300 bg-slate-50">
        <div className="flex items-center justify-between border-b border-slate-200 bg-slate-100 px-3 py-2 text-slate-700">
          <h2 id="source-query-heading" className="text-[12px] font-semibold">Search legislation</h2>
          <span className="text-[11px] text-slate-600">Enter at least 2 characters</span>
        </div>
        <div className="p-3 sm:p-4">
          <div className="flex flex-col items-stretch gap-2.5 sm:flex-row sm:items-center">
            <div className="flex min-h-10 flex-1 items-center gap-2.5 border border-slate-300 bg-white px-3 focus-within:border-[color:var(--accent)] focus-within:ring-1 focus-within:ring-[color:var(--accent)]">
              <Icon name="search" size={18} className="text-slate-400" />
              <input type="search" aria-label="Search obligations and legislation" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search obligation text, instrument title, or statutory reference" className="w-full bg-transparent text-[13.5px] text-slate-900 outline-none placeholder:text-slate-400" />
              {q && <button type="button" onClick={() => setQ("")} className="text-[12px] font-medium text-slate-400 hover:text-slate-700">Clear</button>}
            </div>
            {searchText.length >= 2 && companyId !== null && (
              <Button
                variant="outline"
                icon="doc"
                className="justify-center sm:shrink-0"
                disabled={structuredExport === undefined || structuredExport.rows.length === 0}
                onClick={exportStructuredResults}
              >
                {structuredExport === undefined
                  ? "Compiling workbook..."
                  : structuredExport.rows.length === 0
                    ? "No export rows"
                    : `Export context (${formatCountLabel(structuredExport.rows.length, "row")})`}
              </Button>
            )}
          </div>

          <div className="mt-3 flex flex-col gap-3 border-t border-slate-200 pt-3 lg:flex-row lg:items-center">
            <div className="flex min-w-0 items-center gap-1 overflow-x-auto" role="group" aria-label="Jurisdiction filters">
              <span className="mr-1 shrink-0 text-[11.5px] font-semibold text-slate-600">Jurisdiction</span>
              <Chip active={juris === "ALL"} onClick={() => setJuris("ALL")} dark>ALL</Chip>
              {JURISDICTIONS.map((j) => (
                <Chip key={j} active={juris === j} onClick={() => setJuris(j)} dark>{jurisShort(j)}</Chip>
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-1 lg:ml-auto">
              <span className="mr-1 text-[11.5px] font-semibold text-slate-600">Type</span>
              {TYPE_FILTER_OPTIONS.map((option) => (
                <Chip
                  key={option.value}
                  active={legislationBrowserTypeFilterToInput(type) === option.value}
                  onClick={() => setType(legislationBrowserTypeFilterFromInput(option.value))}
                >
                  {option.label}
                </Chip>
              ))}
              <label className="ml-2 flex cursor-pointer select-none items-center gap-1.5 border-l border-slate-300 pl-3 text-[12px] font-medium text-slate-600">
                <input type="checkbox" checked={subOnly} onChange={(e) => setSubOnly(e.target.checked)} className="h-3.5 w-3.5 accent-[var(--accent)]" />
                Monitored only
              </label>
            </div>
          </div>
          {structuredExport !== undefined && structuredExport.rows.length > 0 && (
            <div className="mt-3 flex items-start gap-1.5 border-l-2 border-sky-500 bg-sky-50 px-2.5 py-2 text-[11.5px] text-sky-900">
              <Icon name="info" size={13} className="mt-0.5 shrink-0" />
              Context workbook includes related company-document and legislation rows, including non-matching clauses, plus {formatCountLabel(structuredExport.mappings.length, "normalized mapping")}. It does not apply the jurisdiction/type filters. Use “Export all matching clauses” for the filtered source results.
            </div>
          )}
        </div>
      </section>

      {searchText.length >= 2 && <div className="mb-4 space-y-2 rounded border bg-white p-4"><Button variant="outline" disabled={!sectionMatches?.length} onClick={exportMatchingResults}>Export all matching clauses{sectionMatches ? ` (${sectionMatches.length})` : ""}</Button><p className="text-xs text-slate-600">Source-clause search covers accessible monitored sources within the jurisdiction/type filters. Exact words and phrases; use * for word prefixes. The monitored toggle applies to the title register below.</p>{completeSearch.error ? <p role="alert" className="text-rose-700">{completeSearch.error}</p> : !sectionMatches && <p role="status">Scanning source pages… {completeSearch.found} matches found so far. Count and export become available when complete.</p>}</div>}
      {sectionSearchView.kind !== "hidden" && !completeSearch.error && (
        <SectionSearchResults
          key={`${searchText}-${juris}-${type.kind === "all" ? "all" : type.legislationKind}`}
          view={sectionSearchView}
          selectedSectionIds={selectedSectionIds}
          selectedDrafts={selectedDrafts}
          selectedCount={selectedSectionMatches.length}
          obligationSetName={obligationSetName}
          saveState={saveState}
          saveError={saveError}
          savedObligationSetId={savedObligationSetId}
          canSaveSet={canWrite && companyId !== null && selectedSectionMatches.length > 0}
          onNameChange={updateObligationSetName}
          onToggleSelection={toggleSectionSelection}
          onUpdateDraft={updateSelectedDraft}
          onExportSelected={exportSelectedObligations}
          onSaveSelected={saveSelectedObligationSet}
          onContinueToMapping={(obligationSetId) => navigate(`/documents/import?obligationSet=${encodeURIComponent(obligationSetId)}`)}
          onOpen={(match) => navigate(`/legislation/${match.legislation._id}?section=${match.section._id}&q=${encodeURIComponent(searchText)}`)}
        />
      )}

      <div className="mb-2.5 flex items-end justify-between border-b border-slate-300 pb-2">
        <h2 className="text-[15px] font-semibold text-slate-900">Source instruments</h2>
        <div className="text-[12px] text-slate-600">
          {legislation === undefined ? "Loading instruments…" : `${formatCountLabel(rows.length, "instrument")} visible`}
        </div>
      </div>

      <Card className="divide-y divide-slate-200 overflow-hidden border-slate-300">
        {rows.slice(0, 300).map((r) => {
          const subscribed = subscribedIds.has(r._id);
          const rowView = legislationBrowserRowView(r);
          return (
            <button key={r._id} onClick={() => navigate(`/legislation/${r._id}`)} className="group flex w-full items-center gap-3 px-3 py-3 text-left transition hover:bg-sky-50/70 sm:px-4">
              <div className="shrink-0"><JurisBadge code={r.jurisdiction} /></div>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className={cx("truncate text-[14px] font-medium", rowView.titleToneClass)}>{r.title}</span>
                  {r.legacySnapshot && <span className="rounded bg-amber-50 px-2 py-0.5 text-[11px] text-amber-800">Historical snapshot</span>}
                  {rowView.statusBadgeKind !== null && <StatusBadge kind={rowView.statusBadgeKind} />}
                  {subscribed && (
                    <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700">
                      <Icon name="check" size={12} /> Monitored
                    </span>
                  )}
                </div>
                <div className="mt-1 text-[12px] text-slate-500">{r.reference}</div>
              </div>
              <div className="hidden shrink-0 items-center gap-2 sm:flex">
                <TypeBadge type={r.kind} />
              </div>
              <Icon name="arrowRight" size={15} className="shrink-0 text-slate-300 group-hover:text-[var(--accent)]" />
            </button>
          );
        })}
        {legislation !== undefined && rows.length === 0 && (
          <div className="py-16 text-center text-sm text-slate-400">No instruments match your filters.</div>
        )}
      </Card>
    </div>
  );
}

function RegisterReadout({ label, value, active = false }: { label: string; value: string; active?: boolean }) {
  return (
    <div className="flex min-w-0 items-baseline gap-1.5 border-r border-slate-300 px-3 first:pl-0 last:border-r-0 last:pr-0">
      <span className="whitespace-nowrap text-slate-600">{label}</span>
      <span className={cx("whitespace-nowrap font-semibold text-slate-800", active && "text-sky-700")} aria-live="polite" aria-atomic="true">{value}</span>
    </div>
  );
}

function SectionSearchResults({
  view,
  selectedSectionIds,
  selectedDrafts,
  selectedCount,
  obligationSetName,
  saveState,
  saveError,
  savedObligationSetId,
  canSaveSet,
  onNameChange,
  onToggleSelection,
  onUpdateDraft,
  onExportSelected,
  onSaveSelected,
  onContinueToMapping,
  onOpen,
}: {
  view: VisibleSectionSearchView;
  selectedSectionIds: ReadonlySet<Id<"sections">>;
  selectedDrafts: Readonly<Record<string, SelectedObligationDraft>>;
  selectedCount: number;
  obligationSetName: string;
  saveState: ObligationSetSaveState;
  saveError: string | null;
  savedObligationSetId: Id<"obligationSets"> | null;
  canSaveSet: boolean;
  onNameChange: (name: string) => void;
  onToggleSelection: (sectionId: Id<"sections">) => void;
  onUpdateDraft: (sectionId: Id<"sections">, patch: Partial<SelectedObligationDraft>) => void;
  onExportSelected: () => void;
  onSaveSelected: () => void;
  onContinueToMapping: (obligationSetId: Id<"obligationSets">) => void;
  onOpen: (match: SectionSearchResult) => void;
}) {
  const [visibleCount, setVisibleCount] = useState(50);
  const defaultSetName = view.kind === "ready" ? obligationSetDefaultName(view.query) : "Selected obligations";
  const saveNotice = saveState === "saved"
    ? "Obligation set saved."
    : saveState === "error"
      ? saveError ?? "Could not save obligation set."
      : null;

  return (
    <section className="mb-6" aria-labelledby="obligation-results-heading">
      <div className="flex flex-wrap items-end justify-between gap-2 border-b border-slate-300 pb-2">
        <h2 id="obligation-results-heading" className="text-[15px] font-semibold text-slate-900">Obligation clauses</h2>
        <div className="text-[12px] text-slate-500">{view.countLabel} · {selectedCount} selected</div>
      </div>
      {view.kind === "ready" && (
        <div className="border-x border-b border-slate-300 bg-slate-50 p-3">
          <div className="flex flex-col gap-2.5 lg:flex-row lg:items-center">
            <div className="min-w-0 flex-1">
              <div className="text-[12px] font-semibold text-slate-700">Build an obligation set</div>
              <p className="mt-0.5 text-[12px] text-slate-600">Select clauses, attach operational metadata, then save the set into document mapping.</p>
            </div>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <input
                value={obligationSetName}
                onChange={(event) => onNameChange(event.target.value)}
                placeholder={defaultSetName}
                className="h-9 min-w-0 border border-slate-300 bg-white px-2.5 text-[12.5px] text-slate-800 outline-none placeholder:text-slate-400 focus:border-[color:var(--accent)] focus:ring-1 focus:ring-[color:var(--accent)] sm:w-60"
                aria-label="Obligation set name"
              />
              <button
                type="button"
                disabled={!canSaveSet || saveState === "saving"}
                onClick={onSaveSelected}
                className="h-9 border border-slate-900 bg-slate-900 px-3 text-[12px] font-semibold text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:border-slate-300 disabled:bg-slate-200 disabled:text-slate-500"
              >
                {saveState === "saving" ? "Saving set…" : `Save set (${selectedCount})`}
              </button>
              <button
                type="button"
                disabled={selectedCount === 0}
                onClick={onExportSelected}
                className="h-9 border border-slate-300 bg-white px-3 text-[12px] font-semibold text-slate-700 hover:border-slate-500 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-45"
              >
                Export CSV
              </button>
            </div>
          </div>
        </div>
      )}
      {saveNotice !== null && (
        <div
          role={saveState === "error" ? "alert" : "status"}
          className={cx(
            "flex flex-col justify-between gap-2 border-x border-b px-3 py-2.5 text-[12.5px] sm:flex-row sm:items-center",
            saveState === "error" ? "border-red-300 bg-red-50 text-red-700" : "border-emerald-300 bg-emerald-50 text-emerald-800",
          )}
        >
          <span className="inline-flex items-center gap-1.5 font-medium">
            <Icon name={saveState === "error" ? "info" : "check"} size={14} />
            {saveNotice}
          </span>
          {savedObligationSetId !== null && (
            <button
              type="button"
              onClick={() => onContinueToMapping(savedObligationSetId)}
              className="inline-flex min-h-8 items-center justify-center gap-1.5 border border-emerald-700 bg-emerald-700 px-3 text-[12px] font-semibold text-white hover:bg-emerald-800"
            >
              Create document and map set <Icon name="arrowRight" size={13} />
            </button>
          )}
        </div>
      )}
      <Card className="mt-2.5 divide-y divide-slate-200 overflow-hidden border-slate-300">
        {view.kind === "loading" ? (
          <div className="px-5 py-6 text-sm text-slate-400">Searching obligation snippets...</div>
        ) : view.kind === "empty" ? (
          <div className="px-5 py-8 text-center text-sm text-slate-400">{view.message}</div>
        ) : (
          view.matches.slice(0, visibleCount).map((match) => {
            const selected = selectedSectionIds.has(match.section._id);
            const draft = selectedDrafts[String(match.section._id)] ?? emptySelectedObligationDraft();
            return (
              <div key={match.section._id} className={cx("group flex w-full items-start gap-3 px-3 py-3.5 transition sm:px-4", selected ? "bg-sky-50/60" : "hover:bg-slate-50")}>
                <label className="mt-1 flex h-5 w-5 shrink-0 items-center justify-center">
                  <input
                    type="checkbox"
                    checked={selected}
                    onChange={() => onToggleSelection(match.section._id)}
                    className="h-4 w-4 border-slate-300 accent-[var(--accent)]"
                    aria-label={`Stage ${match.section.number} ${match.section.title}`}
                  />
                </label>
                <div className="min-w-0 flex-1">
                  <button
                    type="button"
                    onClick={() => onOpen(match)}
                    className="flex w-full min-w-0 items-start gap-4 text-left"
                  >
                    <div className="mt-0.5 shrink-0"><JurisBadge code={match.legislation.jurisdiction} /></div>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="truncate text-[14px] font-semibold text-slate-900">{match.legislation.title}</span>
                        <TypeBadge type={match.legislation.kind} />
                      </div>
                      <div className="mt-1 flex flex-wrap items-center gap-2 text-[12px] text-slate-400">
                        <span className="text-[12px] text-slate-500">{match.legislation.reference}</span>
                        <span className="h-1 w-1 rounded-full bg-slate-300" />
                        <span className="font-medium text-slate-500">{match.section.number} {match.section.title}</span>
                      </div>
                      {match.legislation.historical && <p className="mt-1 text-xs text-amber-800">Historical snapshot · current status unverified</p>}
                      <p className="mt-2 line-clamp-2 text-[13px] leading-relaxed text-slate-600"><SearchText text={match.snippet} query={view.query} /></p>
                    </div>
                    <Icon name="arrowRight" size={15} className="mt-1 shrink-0 text-slate-300 group-hover:text-[var(--accent)]" />
                  </button>
                  {selected && (
                    <SelectedObligationFields
                      draft={draft}
                      onChange={(patch) => onUpdateDraft(match.section._id, patch)}
                    />
                  )}
                </div>
              </div>
            );
          })
        )}
      </Card>
      {view.kind === "ready" && visibleCount < view.matches.length && <Button variant="outline" className="mt-3" onClick={() => setVisibleCount(count => count + 50)}>Show more clauses ({Math.min(visibleCount, view.matches.length)} of {view.matches.length} shown)</Button>}
    </section>
  );
}

function SelectedObligationFields({
  draft,
  onChange,
}: {
  draft: SelectedObligationDraft;
  onChange: (patch: Partial<SelectedObligationDraft>) => void;
}) {
  return (
    <div className="mt-3 grid gap-2 border border-slate-300 bg-slate-50 p-3 md:grid-cols-2 lg:grid-cols-4">
      <LabeledMiniField label="Responsible person">
        <input
          value={draft.ownerName}
          onChange={(event) => onChange({ ownerName: event.target.value })}
          className={SELECTED_OBLIGATION_FIELD_CLASS}
        />
      </LabeledMiniField>
      <LabeledMiniField label="Status">
        <select
          value={draft.status}
          onChange={(event) => onChange({ status: event.target.value as ObligationSetItemStatus })}
          className={SELECTED_OBLIGATION_FIELD_CLASS}
        >
          {OBLIGATION_SET_ITEM_STATUSES.map((status) => (
            <option key={status} value={status}>{obligationSetItemStatusLabel(status)}</option>
          ))}
        </select>
      </LabeledMiniField>
      <LabeledMiniField label="Evidence/control ref">
        <input
          value={draft.evidenceControlReference}
          onChange={(event) => onChange({ evidenceControlReference: event.target.value })}
          className={SELECTED_OBLIGATION_FIELD_CLASS}
        />
      </LabeledMiniField>
      <LabeledMiniField label="Notes">
        <input
          value={draft.notes}
          onChange={(event) => onChange({ notes: event.target.value })}
          className={SELECTED_OBLIGATION_FIELD_CLASS}
        />
      </LabeledMiniField>
    </div>
  );
}

function LabeledMiniField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="min-w-0 text-[11.5px] font-semibold text-slate-500">
      <span className="mb-1 block truncate">{label}</span>
      {children}
    </label>
  );
}

const obligationSetItemFromMatch = ({
  match,
  topic,
  draft,
}: {
  match: SectionSearchResult;
  topic: string;
  draft: SelectedObligationDraft;
}): ObligationSetItemInput<Id<"legislation">, Id<"sections">> => ({
  legislationId: match.legislation._id,
  sectionId: match.section._id,
  legislationTitle: match.legislation.title,
  legislationReference: match.legislation.reference,
  jurisdiction: match.legislation.jurisdiction,
  sectionNumber: match.section.number,
  sectionTitle: match.section.title,
  snippet: match.snippet,
  topic,
  ownerName: draft.ownerName,
  status: draft.status,
  evidenceControlReference: draft.evidenceControlReference,
  notes: draft.notes,
});


function Chip({ active, onClick, children, dark }: { active: boolean; onClick: () => void; children: React.ReactNode; dark?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cx(
        "shrink-0 rounded-sm px-2.5 py-1 text-[12px] font-medium ring-1 transition",
        active
          ? dark
            ? "bg-slate-900 text-white ring-slate-900"
            : "bg-[var(--accent-soft)] text-[var(--accent-dark)] ring-[color:var(--accent)]/25"
          : "bg-white text-slate-600 ring-slate-200 hover:ring-slate-300",
      )}
    >
      {children}
    </button>
  );
}
